<template>
  <div :class="fadeInClass">
    <QuestionAsker v-if="showQuestionAsker" :questions="questionsToAsk" @complete="handleQuestionComplete"
      @cancel="handleQuestionCancel" />
    
    <!-- Countdown Ring -->
    <div class="countdown-indicator" v-if="!receiptSent && paymentType === 'CREDIT'">
      <svg class="countdown-ring" width="32" height="32" viewBox="0 0 32 32">
        <circle class="countdown-ring-bg" r="14" cx="16" cy="16"></circle>
        <circle 
          class="countdown-ring-progress" 
          r="14" 
          cx="16" 
          cy="16" 
          :style="{ strokeDashoffset: progressOffset }"
        ></circle>
      </svg>
      <span class="countdown-text">{{ remainingSeconds }}</span>
    </div>

    <TwoPartLayout>
      <template v-slot:two>
        <div>
          <template v-if="!receiptSent && paymentType === 'CREDIT'">
            <h3>{{ $t('receipt') }}</h3>
            <h3 v-if="displayOrderNumber">
              {{ $t('orderNumber') }}:
              <span class="badge rounded-pill bg-primary text-light order-number">
                {{ displayOrderNumber }}
              </span>
            </h3>
            <p>{{ $t('receiptText') }}</p><br />
            <br />
            <br />
            <button @click="sendEmail" :disabled="loading === 'email'"
              class="btn btn-lg btn-outline-primary flex-grow-1">
              <SvgIcon v-if="loading !== 'email'" width="2.15rem" height="2.15rem" name="email" />
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ $t('emailReceipt') }}
            </button><br />
            <button @click="sms" v-if="isUsLocale" :disabled="loading === 'phone'" class="btn btn-lg btn-outline-primary flex-grow-1">
              <SvgIcon v-if="loading !== 'phone'" width="2.15rem" height="2.15rem" name="sms" />
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ $t('smsReceipt') }}
            </button><br />

            <button v-if="receiptData && hasSDK && hasPrinterConfig && terminalConfig?.printing?.allowManualReceiptPrint !== false" @click="printReceipt" :disabled="loading === 'print'" class="btn btn-lg btn-outline-primary flex-grow-1">
              <SvgIcon v-if="loading !== 'print'" width="2.15rem" height="2.15rem" name="print" />
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ $t('printReceipt') || 'Print Receipt' }}
            </button><br v-if="receiptData && hasSDK && hasPrinterConfig && terminalConfig?.printing?.allowManualReceiptPrint !== false" />

            <br />

            <div v-if="config.ratingPromptEnabled">
              <p>{{ $t('ratingPrompt') || 'Please rate today\'s experience' }}</p>
              <div style="display: block; width: 100%; margin-left: -0.5rem;">
                <RatingComponent :orderRef="appRef" />
              </div>
            </div>
            <div v-else>
              <button @click="no" class="btn btn-lg btn-outline-primary flex-grow-1">
                {{ $t('done') || 'Done' }}
                <SvgIcon name="forward" width="3rem" height="3rem" />
              </button><br />
            </div>
            
          </template>
          <template v-else>
            <h3>{{ $t('complete') || 'Complete' }}</h3>
            <h3 v-if="displayOrderNumber">
              {{ $t('orderNumber') || 'Order Number' }}:
              <span class="badge rounded-pill bg-primary text-light order-number">
                {{ displayOrderNumber }}
              </span>
            </h3>
            <p v-if="paymentType == 'CASH'">{{ $t('payUpFrontInstructions') }}</p>
            <p v-else>{{ $t('thankYou') }}</p><br />
            <button class="btn btn-link text-primary right" @click="newOrder">
              {{ $t('startNewOrder') }}
              <SvgIcon name="forward" width="3rem" height="3rem" />
            </button>
          </template>
        </div>
      </template>
    </TwoPartLayout>

    <!-- Print Receipt Modal -->
    <PrintReceiptModal
      :visible="showPrintModal"
      :state="printModalState"
      :error-message="printErrorMessage"
      @retry="handlePrintRetry"
      @cancel="handlePrintCancel"
    />
  </div>
</template>

<script>
/* eslint-disable no-undef */
import { mapGetters } from "vuex";
import TwoPartLayout from "@/components/global/TwoPartLayout.vue";
import QuestionAsker from "@/components/features/QuestionAsker.vue";
import RatingComponent from "@/components/features/RatingComponent.vue";
import PrintReceiptModal from "@/components/features/dialog/PrintReceiptModal.vue";
import { orderInactive } from "@/utils/wsHelper"; // Import the function

export default {
  name: "CompleteView",
  data() {
    return {
      lastAppRef: null,
      complete: false,
      receiptSent: false,
      receiptPrinted: false,
      showQuestionAsker: false,
      questionsToAsk: [],
      receiptType: null,
      loading: null,
      fadeInClass: "fade-in",
      idleTimeout: null,
      remainingSeconds: 5,
      countdownInterval: null,
      startTime: null,
      currentPrintId: null,
      // Print modal state
      showPrintModal: false,
      printModalState: 'printing', // 'printing' | 'error'
      printErrorMessage: '',
      pickupChitAutoPrinted: false,
    };
  },
  components: {
    TwoPartLayout,
    QuestionAsker,
    RatingComponent,
    PrintReceiptModal,
  },
  computed: {
    ...mapGetters(["hasSDK", "screenOrientation"]),
    ...mapGetters("order", ["appRef", "paymentType", "email", "phone", "gpattTicket", "receiptData", "orderTypeId", "confirmedOrderNumber", "submittedOrderNumber"]),
    ...mapGetters("location", ["config", "locationName"]),
    ...mapGetters("terminal", ["printerIp", "hasPrinter", "terminalConfig"]),
    ...mapGetters({
      isUsLocale: 'isUsLocale'
    }),
    displayOrderNumber() {
      // STM uses gpattTicket.orderNumber; OLO uses callNumber from CONFIRMED webhook
      if (this.gpattTicket?.orderNumber) return this.gpattTicket.orderNumber;
      const usePosOrderNumber =
        this.config?.rails === 'olo' &&
        (this.config?.oloUsePosOrderNumber === true || this.config?.oloUseCallNumberForOrderNumber === true); // backward compatible
      if (usePosOrderNumber && this.confirmedOrderNumber) return this.confirmedOrderNumber;
      // OLO fallback: provisional number generated at submission time (pre-POS-confirmation era behavior)
      if (this.config?.rails === 'olo' && this.submittedOrderNumber) return this.submittedOrderNumber;
      return null;
    },
    timeoutSeconds() {
      return this.config?.completeTimeoutSeconds || 5;
    },
    progressOffset() {
      const circumference = 2 * Math.PI * 14; // radius = 14
      const progress = this.remainingSeconds / this.timeoutSeconds;
      return circumference * (1 - progress);
    },
    hasPrinterConfig() {
      return this.hasPrinter;
    },
    shouldPreventTimeout() {
      // Prevent timeout from starting if print modal is open
      return this.showPrintModal;
    },
    currentOrderType() {
      if (!this.config || !this.config.orderTypes || !this.orderTypeId) return null;
      return this.config.orderTypes.find(ot => ot.id === this.orderTypeId);
    },
  },
  methods: {
    maybeAutoPrintPickupChit() {
      // Only auto-print once, and only when we have an order number to print.
      if (this.pickupChitAutoPrinted) return;

      const printing = this.terminalConfig?.printing || {
        active: true,
        autoPrintReceipt: this.paymentType === 'CREDIT' && this.config.rails === 'olo', // Legacy default
        printPickupChit: false
      };

      const orderTypePrintPickupChit = this.currentOrderType?.printPickupChit;

      if ((orderTypePrintPickupChit || printing.printPickupChit) && this.hasPrinterConfig && this.displayOrderNumber) {
        this.pickupChitAutoPrinted = true;
        this.printOrderChit();
      }
    },
    askForReceipt(receiptType) {
      this.receiptType = receiptType;
      this.questionsToAsk = [{
        title: receiptType === "email" ? "Enter your email address" : "Enter your phone number",
        text: receiptType === "email" ? "We will send the receipt to your email." : "We will send the receipt as an SMS.",
        inputType: receiptType == "email" ? "email" : "tel",
        placeholder: receiptType === "email" ? "example@example.com" : "(555) 555-5555",
        optIn: false,
        optInText: "Save this for future receipts",
        validationRegex: receiptType === "email" ? "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$" : "^[0-9]{3}-[0-9]{3}-[0-9]{4}$",
        required: true,
        orderProperty: receiptType // Add this line to specify which property to use
      }];
      this.showQuestionAsker = true;
    },
    async handleQuestionComplete(response) {
      this.showQuestionAsker = false;

      const answer = response[0]?.answer || null;
      // Logic to send receipt
      try {
        this.$store.dispatch("order/sendReceipt", { to: answer, type: this.receiptType });
        this.receiptSent = true;
      } catch (error) {
        console.error("Error sending receipt", error);
        this.receiptSent = true;
      } finally {
        this.receiptSent = true;
      }
    },
    handleQuestionCancel() {
      this.showQuestionAsker = false;
    },
    async sendEmail() {
      this.loading = 'email';
      try {
        await this.askForReceipt("email");
      } finally {
        this.loading = null;
      }
    },
    async sms() {
      this.loading = 'phone';
      try {
        await this.askForReceipt("phone");
      } finally {
        this.loading = null;
      }
    },
    async printReceipt() {
      this.loading = 'print';
      try {
        if (!this.receiptData) {
          console.error('printReceipt: No receiptData available');
          this.$store.dispatch("dialog/openDialog", {
            title: "Print Error", 
            message: "No printed receipt data available. Please try again.",
            showCancel: false,
            okText: "OK"
          });
          this.loading = null;
          return;
        }

        this.currentPrintId = Shift4Kiosk.printReceipt(JSON.stringify(this.receiptData));
        
        // Loading state will be cleared by the success/error callbacks

      } catch (error) {
        console.error('Error in printReceipt:', error);
        this.loading = null;
        this.$store.dispatch("dialog/openDialog", {
          title: "Print Error",
          message: `Failed to print receipt: ${error.message || 'Unknown error'}`,
          showCancel: false,
          okText: "OK"
        });
      }
    },
    initializeSDKCallbacks() {
      if (typeof Shift4Kiosk !== 'undefined') {
        Shift4Kiosk.onPrintSuccess = (printResult) => {
          if (printResult.id === this.currentPrintId) {
            this.loading = null; // Clear loading state
            this.showPrintModal = false; // Close modal
            // Printing is not the same as "receipt sent" (email/sms). Keep the receipt options screen available.
            this.receiptPrinted = true;
            
            // Start timeout after successful print
            this.resetIdleTimeout();
            
            // We no longer call this.no() here because we want to stay on the complete screen
            // until the idle timeout or the user manually clicks a button.
          } else {
            console.warn('Unexpected print success for ID:', printResult.id);
          }
        };

        Shift4Kiosk.onPrintError = (printError) => {
          if (printError.id === this.currentPrintId) {
            this.loading = null; // Clear loading state
            this.printModalState = 'error';
            this.printErrorMessage = printError.error || 'Unknown error occurred during printing';
            console.error('Print error:', printError);
          } else {
            console.warn('Unexpected print failure for ID:', printError.id);
          }
        };
      }
    },
    checkAndAutoPrintReceipt() {
      // Use terminal config if available, otherwise use defaults
      const printing = this.terminalConfig?.printing || {
        active: true,
        autoPrintReceipt: this.paymentType === 'CREDIT' && this.config.rails === 'olo', // Legacy default
        printPickupChit: false
      };
      
      const orderTypePrintPickupChit = this.currentOrderType?.printPickupChit;
      
      let autoPrintDelay = 0;

      // Check for Pickup Chit - print first if enabled
      if ((orderTypePrintPickupChit || printing.printPickupChit) && this.hasPrinterConfig && this.displayOrderNumber) {
        this.pickupChitAutoPrinted = true;
        this.printOrderChit();
        autoPrintDelay = 1500; // Give the chit print call a head start before the full receipt
      }

      // Check if we should auto-print full receipt
      if (printing.autoPrintReceipt && this.hasPrinterConfig && this.receiptData) {
        setTimeout(() => {
          this.startAutoPrint();
        }, autoPrintDelay);
        return;
      }

      // If nothing was auto-printed, reset timeout normally
      if (autoPrintDelay === 0) {
        this.resetIdleTimeout();
      }
    },
    async printOrderChit() {
      if (!this.displayOrderNumber) {
        console.warn('Cannot print pickup chit: No order number available');
        return;
      }

      const chitData = {
        gutter: 2,
        printerWidth: this.config?.print_width || 48,
        lines: [
          {
            type: "Text",
            text: (this.locationName || "Location").toUpperCase(),
            align: "CENTER",
            bold: true,
            size: "NORMAL"
          },
          {
            type: "Text",
            text: (this.currentOrderType?.name || "").toUpperCase(),
            align: "CENTER",
            bold: true,
            size: "NORMAL"
          },
          { type: "Empty", count: 1 },
          {
            type: "Text",
            text: `${this.$t('orderNumber') || "Order Number"} ${this.displayOrderNumber}`,
            align: "CENTER",
            bold: true,
            size: "LARGE"
          },
          { type: "Empty", count: 1 },
          {
            type: "Text",
            text: new Date().toLocaleString(this.config?.locale || 'en-US'),
            align: "CENTER",
            size: "SMALL"
          },
          { type: "Empty", count: 3 }
        ]
      };

      try {
        this.currentPrintId = Shift4Kiosk.printReceipt(JSON.stringify(chitData));
        this.showPrintModal = true;
        this.printModalState = 'printing';
        this.printErrorMessage = '';
      } catch (error) {
        console.error('Error printing pickup chit:', error);
        this.showPrintModal = false;
        this.resetIdleTimeout();
      }
    },
    startAutoPrint() {
      this.showPrintModal = true;
      this.printModalState = 'printing';
      this.printErrorMessage = '';
      this.printReceipt();
    },
    handlePrintRetry() {
      this.printModalState = 'printing';
      this.printErrorMessage = '';
      this.printReceipt();
    },
    handlePrintCancel() {
      this.showPrintModal = false;
      this.printModalState = 'printing';
      this.printErrorMessage = '';
      
      // Start timeout after user cancels print
      this.resetIdleTimeout();
    },
    no() {
      this.complete = true;
      this.$router.push({ name: "start" });
    },
    newOrder() {
      this.$router.push({ name: "start" });
    },
    resetIdleTimeout() {
      // Don't start timeout if print modal is open
      if (this.shouldPreventTimeout) {
        return;
      }

      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout);
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      const timeoutSeconds = this.config?.completeTimeoutSeconds || 5;
      this.idleTimeout = setTimeout(() => {
        this.$router.push({ name: "start" });
      }, timeoutSeconds * 1000);
      
      // Start visual countdown
      this.startCountdown();
    },
    startCountdown() {
      const timeoutSeconds = this.config?.completeTimeoutSeconds || 5;
      this.remainingSeconds = timeoutSeconds;
      this.startTime = Date.now();
      
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      
      this.countdownInterval = setInterval(() => {
        const elapsed = (Date.now() - this.startTime) / 1000;
        this.remainingSeconds = Math.max(Math.ceil(timeoutSeconds - elapsed), 0);
        
        if (this.remainingSeconds <= 0) {
          clearInterval(this.countdownInterval);
        }
      }, 100); // Update every 100ms for smooth animation
    },
  },
  watch: {
    // For OLO, POS call/order number may arrive after mount (after CONFIRMED). Re-attempt chit printing when available.
    displayOrderNumber() {
      this.maybeAutoPrintPickupChit();
    }
  },
  async mounted() {
    this.lastAppRef = this.$store.getters["order/appRef"];
    this.$store.dispatch("order/setTimeoutActive", true);
    setTimeout(() => {
      this.fadeInClass = "fade-in";
    }, 1000);

    // Call orderInactive when the component is mounted
    const locationId = this.$store.state.locationId; // Assuming locationId is stored in Vuex
    const orderRef = this.appRef;
    orderInactive(locationId, orderRef);

    // Add event listeners for user interactions
    window.addEventListener('mousemove', this.resetIdleTimeout);
    window.addEventListener('keydown', this.resetIdleTimeout);
    window.addEventListener('click', this.resetIdleTimeout);
    window.addEventListener('touchstart', this.resetIdleTimeout);

    this.initializeSDKCallbacks();

    // Small delay to ensure store state is fully settled and SDK is ready
    setTimeout(() => {
      this.checkAndAutoPrintReceipt();
    }, 500);
  },
  beforeUnmount() {
    // Clean up event listeners and timeout when the component is destroyed
    window.removeEventListener('mousemove', this.resetIdleTimeout);
    window.removeEventListener('keydown', this.resetIdleTimeout);
    window.removeEventListener('click', this.resetIdleTimeout);
    window.removeEventListener('touchstart', this.resetIdleTimeout);
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Clean up Shift4Kiosk callbacks
    if (typeof Shift4Kiosk !== 'undefined') {
      Shift4Kiosk.onPrintSuccess = null;
      Shift4Kiosk.onPrintError = null;
    }

    // Close print modal if open
    this.showPrintModal = false;
  },
};
</script>

<style scoped>
.hold {
  position: absolute;
  top: 0;
}

.countdown-indicator {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
}

.countdown-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

.countdown-ring-bg {
  fill: none;
  stroke: rgba(0, 0, 0, 0.1);
  stroke-width: 2;
}

.countdown-ring-progress {
  fill: none;
  stroke: var(--brand-color, #007bff);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 87.96; /* 2 * π * 14 */
  transition: stroke-dashoffset 0.1s ease;
}

.countdown-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 600;
  color: var(--brand-color, #007bff);
  pointer-events: none;
}

.auto-dismiss-hint {
  text-align: center;
  margin-top: 2rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.auto-dismiss-hint small {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.auto-dismiss-hint i {
  opacity: 0.7;
}
</style>
